import Dexie, { type EntityTable } from 'dexie'
import type { Organization } from '../types'

export interface LocalProduct {
  id: string
  workspace_id: string
  name: string
  code: string
  barcode: string | null
  unit: string
  sell_price: number
  cost_price: number
  current_stock: number
  archived: boolean
  category: string | null
  created_at: string
  purchase_unit: string | null
  pieces_per_unit: number | null
  pack_sell_price: number | null
  is_bundle: boolean
  bundle_items: { component_product_id: string; component_name: string; qty: number }[]
  expiries: { id: string; expires_at: string; qty: number | null; day_known: boolean }[]
}

export interface LocalSession {
  id: string
  workspace_id: string
  user_id: string
  type: 'order' | 'restock' | 'adjustment'
  is_credit?: boolean
  code: string | null
  reason?: string | null
  total: number | null
  completed_at: string | null
  voided_at?: string | null
  created_at: string
}

export interface LocalSessionLine {
  id: string
  session_id: string
  product_id: string
  qty: number
  price_snapshot: number
  cost_snapshot: number
  product_name?: string
  product_code?: string
}

export interface OfflineQueueItem {
  id: string
  procedure: string
  input: object
  created_at: string
  synced_at: string | null
  workspace_id?: string
  // Set when the server definitively rejected the item (non-network error).
  // Dead items are excluded from the pending count and never re-flushed.
  failed_at?: string | null
  // Counts server-error retries (not transport failures). Capped at 10; dead after.
  attempts?: number
  // Human-readable server rejection reason stored when dead-lettering.
  error_message?: string | null
}

export interface LocalDebtor {
  id: string
  workspace_id: string
  name: string
}

export interface MetaEntry {
  key: string
  value: string
}

class ShelviDB extends Dexie {
  products!: EntityTable<LocalProduct, 'id'>
  sessions!: EntityTable<LocalSession, 'id'>
  session_lines!: EntityTable<LocalSessionLine, 'id'>
  offline_queue!: EntityTable<OfflineQueueItem, 'id'>
  organizations!: EntityTable<Organization, 'id'>
  meta!: EntityTable<MetaEntry, 'key'>
  credit_debtors!: EntityTable<LocalDebtor, 'id'>

  constructor() {
    super('shelvi_db')
    this.version(1).stores({
      products: 'id, org_id, code, name',
      sessions: 'id, org_id, user_id, type, created_at',
      session_lines: 'id, session_id, product_id',
      offline_queue: 'id, table_name, synced_at, created_at',
    })
    this.version(2).stores({
      products: 'id, org_id, code, name',
      sessions: 'id, org_id, type, created_at',
      session_lines: 'id, session_id, product_id',
      offline_queue: 'id, procedure, created_at',
      organizations: 'id, owner_id',
    }).upgrade(tx => tx.table('offline_queue').clear())
    this.version(3).stores({
      products: 'id, org_id, code, name',
      sessions: 'id, org_id, type, created_at, completed_at',
      session_lines: 'id, session_id, product_id',
      offline_queue: 'id, procedure, created_at',
      organizations: 'id, owner_id',
      meta: 'key',
    })
    this.version(4).stores({
      products: 'id, org_id, code, name',
      sessions: 'id, org_id, type, created_at, completed_at',
      session_lines: 'id, session_id, product_id',
      offline_queue: 'id, procedure, created_at',
      organizations: 'id, owner_id',
      meta: 'key',
      credit_debtors: 'id, org_id',
    })
    this.version(5).stores({
      products: 'id, org_id, code, name',
      sessions: 'id, org_id, type, created_at, completed_at',
      session_lines: 'id, session_id, product_id',
      offline_queue: 'id, procedure, created_at, org_id',
      organizations: 'id, owner_id',
      meta: 'key',
      credit_debtors: 'id, org_id',
    })
    // v6: offline_queue gains failed_at (unindexed field — schema unchanged)
    this.version(6).stores({
      products: 'id, org_id, code, name',
      sessions: 'id, org_id, type, created_at, completed_at',
      session_lines: 'id, session_id, product_id',
      offline_queue: 'id, procedure, created_at, org_id',
      organizations: 'id, owner_id',
      meta: 'key',
      credit_debtors: 'id, org_id',
    })
    // v7: rename org_id → workspace_id across all data tables
    this.version(7).stores({
      products: 'id, workspace_id, code, name',
      sessions: 'id, workspace_id, type, created_at, completed_at',
      session_lines: 'id, session_id, product_id',
      offline_queue: 'id, procedure, created_at, workspace_id',
      organizations: 'id, owner_id',
      meta: 'key',
      credit_debtors: 'id, workspace_id',
    }).upgrade(async tx => {
      await tx.table('products').toCollection().modify((p: Record<string, unknown>) => {
        if ('org_id' in p) { p.workspace_id = p.org_id; delete p.org_id }
      })
      await tx.table('sessions').toCollection().modify((s: Record<string, unknown>) => {
        if ('org_id' in s) { s.workspace_id = s.org_id; delete s.org_id }
      })
      await tx.table('credit_debtors').toCollection().modify((d: Record<string, unknown>) => {
        if ('org_id' in d) { d.workspace_id = d.org_id; delete d.org_id }
      })
      await tx.table('offline_queue').toCollection().modify((q: Record<string, unknown>) => {
        if ('org_id' in q) { q.workspace_id = q.org_id; delete q.org_id }
      })
    })
  }
}

export const db = new ShelviDB()

// Wipes every local table. Must run whenever the signed-in user changes so
// one account's data (including unsynced queue items) can never leak into
// or be replayed under another account on the same device.
export async function clearLocalData() {
  await Promise.all([
    db.products.clear(),
    db.sessions.clear(),
    db.session_lines.clear(),
    db.offline_queue.clear(),
    db.organizations.clear(),
    db.meta.clear(),
    db.credit_debtors.clear(),
  ])
  // Service-worker runtime cache can hold Supabase responses from the previous
  // account — wipe it alongside IndexedDB on user switch.
  if ('caches' in self) {
    try {
      await caches.delete('supabase-cache')
    } catch {
      // CacheStorage unavailable (e.g. insecure context) — nothing to clear.
    }
  }
}
